import { AnimatePresence, motion } from "framer-motion"
import React, { ReactNode, useEffect, useRef, useState } from "react"

import { useBooleanState } from "@/lib/hooks/use-boolean-state"
import { styled } from "@/stitches"
import { Box } from "@/ui/box"
import { Text } from "@/ui/text"

type TextOverflowContainerProps = {
  children: ReactNode
  maxHeight: number
}

const Overlay = styled(motion.div, {
  display: "grid",
  placeItems: "center",
  position: "absolute",
  bottom: 0,
  left: 0,
  right: 0,
  height: 100,
  bg: `linear-gradient(to bottom, $transparent, $gray1)`,
})

export function TextOverflowContainer({
  children,
  maxHeight,
}: TextOverflowContainerProps) {
  const [contentHeight, setContentHeight] = useState<number>(0)
  const contentExpanded = useBooleanState()
  const contentRef = useRef<HTMLDivElement | null>(null)
  const overflowed = contentHeight > maxHeight

  useEffect(() => {
    const el = contentRef.current
    if (el) {
      setContentHeight(el.clientHeight)
    }
  }, [children, contentRef, maxHeight])

  useEffect(() => {
    const listener = () => {
      const el = contentRef.current
      if (el) {
        setContentHeight(el.clientHeight)
      }
    }

    window.addEventListener("resize", listener)

    return () => {
      window.removeEventListener("resize", listener)
    }
  })

  return (
    <Box
      style={{
        maxHeight: (contentExpanded.isOn ? contentHeight : maxHeight) ?? "auto",
      }}
      css={{
        overflow: "hidden",
        position: "relative",
        transition: "max-height 120ms",
      }}
    >
      <Box ref={contentRef}>
        <Box css={{ mb: "$6" }}>{children}</Box>
        <Text
          as="button"
          link
          button
          color="primary"
          variant="uppercase"
          size="sm"
          weight="bold"
          onClick={contentExpanded.off}
          style={{ visibility: contentExpanded.isOn ? "visible" : "hidden" }}
          css={{ display: "block", mx: "auto" }}
        >
          Zobrazit méně
        </Text>
      </Box>
      {overflowed && (
        <AnimatePresence initial={false}>
          {contentExpanded.isOff && (
            <Overlay
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <Text
                as="button"
                link
                button
                color="primary"
                variant="uppercase"
                size="sm"
                weight="bold"
                onClick={contentExpanded.on}
                css={{ mt: "$16" }}
              >
                Zobrazit více
              </Text>
            </Overlay>
          )}
        </AnimatePresence>
      )}
    </Box>
  )
}
